import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, Button, IconButton } from "@mui/material";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";

const AIDEConsoleSecurityFilter = ({
  open,
  handleClose,
  row,
  countryBrandsData,
  accessList,
  onSaveFilters,
  theme,
}) => {
  const [securityFilterArr, setSecurityFilterArr] = useState([]);
  const [country, setCountry] = useState("");
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);

  // Initialize with existing row security_filters
  useEffect(() => {
    if (row && row.security_filters) {
      setSecurityFilterArr([...row.security_filters]);
    }
  }, [row]);

  // Update brand options when country changes
  useEffect(() => {
    if (country && countryBrandsData && countryBrandsData[country]) {
      const existingCountries = securityFilterArr.map((f) => f.country);
      if (!existingCountries.includes(country)) {
        setBrandOptions(countryBrandsData[country] || []);
      } else {
        // Filter out already-selected brands for this country
        const existing = securityFilterArr.find((f) => f.country === country);
        if (existing) {
          const allBrands = countryBrandsData[country] || [];
          setBrandOptions(
            allBrands.filter((b) => !existing.brands_list.includes(b)),
          );
        }
      }
    } else {
      setBrandOptions([]);
    }
  }, [country, countryBrandsData, securityFilterArr]);

  const countryOptions = countryBrandsData
    ? ["All", ...Object.keys(countryBrandsData).sort()]
    : ["All"];

  const handleAddFilter = () => {
    if (!country || selectedBrands.length === 0) return;

    const existingIdx = securityFilterArr.findIndex(
      (f) => f.country === country,
    );
    if (existingIdx >= 0) {
      // Merge brands
      const updated = [...securityFilterArr];
      const existing = updated[existingIdx];
      const mergedBrands = [
        ...new Set([...existing.brands_list, ...selectedBrands]),
      ];
      updated[existingIdx] = { ...existing, brands_list: mergedBrands };
      setSecurityFilterArr(updated);
    } else {
      setSecurityFilterArr([
        ...securityFilterArr,
        { country, brands_list: selectedBrands },
      ]);
    }

    setCountry("");
    setSelectedBrands([]);
    setBrandOptions([]);
  };

  const handleRemoveFilter = (index) => {
    const updated = securityFilterArr.filter((_, i) => i !== index);
    setSecurityFilterArr(updated);
  };

  const handleClearAll = () => {
    setSecurityFilterArr([]);
    setCountry("");
    setSelectedBrands([]);
  };

  const handleSave = () => {
    onSaveFilters(row, securityFilterArr);
    handleClose();
  };

  const handleCloneFromUser = (userName) => {
    if (!userName || !accessList) return;
    const sourceUser = accessList.find(
      (u) => u.user_id === userName || u.name === userName,
    );
    if (sourceUser && sourceUser.security_filters) {
      setSecurityFilterArr([...sourceUser.security_filters]);
    }
  };

  const userNames = accessList
    ? accessList.filter((u) => u.user_id !== row?.user_id).map((u) => u.user_id)
    : [];

  const isSaveDisabled = securityFilterArr.length === 0;
  const isAddDisabled = !country || selectedBrands.length === 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      className="aide-console-security-filter-dialog"
    >
      <div className="aide-console-sf-header">
        <h3>Security Filter — {row?.user_id || ""}</h3>
        <IconButton onClick={handleClose} size="small" className="close-icon">
          <span className="material-icons-outlined">close</span>
        </IconButton>
      </div>

      <DialogContent className="aide-console-sf-body">
        {/* Clone section */}
        <div className="aide-console-sf-field-group">
          <label>Clone Filters From</label>
          <Autocomplete
            options={userNames}
            getOptionLabel={(opt) => opt}
            disableClearable
            onChange={(e, val) => handleCloneFromUser(val)}
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder="Search user to clone from..."
              />
            )}
          />
        </div>

        {/* Add filter row */}
        <div className="aide-console-sf-field-group">
          <label>Add New Security Filter</label>
          <div className="aide-console-sf-add-row">
            <Autocomplete
              value={country || null}
              options={countryOptions}
              getOptionLabel={(opt) => opt}
              onChange={(e, val) => {
                setCountry(val || "");
                setSelectedBrands([]);
              }}
              style={{ width: "40%" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  placeholder="Select Country"
                />
              )}
            />
            <Autocomplete
              multiple
              value={selectedBrands}
              options={brandOptions}
              getOptionLabel={(opt) => opt}
              onChange={(e, val) => setSelectedBrands(val)}
              style={{ width: "45%" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  placeholder="Select Brands"
                />
              )}
            />
            <Button
              variant="contained"
              size="small"
              disabled={isAddDisabled}
              onClick={handleAddFilter}
              className="add-btn"
            >
              Add
            </Button>
          </div>
        </div>

        {/* Existing filters list */}
        <div className="aide-console-sf-field-group">
          <label>Configured Filters ({securityFilterArr.length})</label>
          {securityFilterArr.map((filter, index) => (
            <div className="aide-console-sf-filter-item" key={index}>
              <span className="country-name">{filter.country}</span>
              <span className="brands-list">
                {filter.brands_list.join(", ")}
              </span>
              <IconButton
                size="small"
                onClick={() => handleRemoveFilter(index)}
                className="delete-icon"
              >
                <span
                  className="material-icons-outlined"
                  style={{ fontSize: 20 }}
                >
                  delete
                </span>
              </IconButton>
            </div>
          ))}

          {securityFilterArr.length === 0 && (
            <div style={{ textAlign: "center", color: "#999", padding: 20 }}>
              No security filters configured. Add at least one filter.
            </div>
          )}
        </div>
      </DialogContent>

      <div className="aide-console-sf-actions">
        <Button
          variant="outlined"
          size="small"
          onClick={handleClearAll}
          className="clear-all-btn"
        >
          Clear All
        </Button>
        <Button
          variant="contained"
          size="small"
          disabled={isSaveDisabled}
          onClick={handleSave}
          className="aide-console-save-btn"
        >
          Save Filters
        </Button>
      </div>
    </Dialog>
  );
};

export default AIDEConsoleSecurityFilter;
